import { z } from 'zod';
import {
  CurrencySchema,
  LocaleSchema,
  MoneySchema,
  ShortIdSchema,
  SlugSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';

/**
 * One-time fees charged on the operator's FIRST invoice only. Drive the
 * recruiter + agent commission splits per the recruiter epic
 * ([[project-agent-recruiter-layer]] + Sprint 80). Both default to 0 so
 * existing plans stay unchanged without migration.
 */
export const PlanFeesSchema = z.object({
  signupSen: MoneySchema.default(0).describe('One-time signup fee, sen'),
  activationSen: MoneySchema.default(0).describe('One-time activation fee, sen'),
});
export type PlanFees = z.infer<typeof PlanFeesSchema>;

/**
 * Plan entitlements — capability flags a plan grants. Distinct from
 * platform-brand `capabilities` (brand-wide, default-TRUE): plan features are
 * per-plan and DEFAULT-OFF, so a tenant only gains one by being on a plan that
 * explicitly turns it on. Adding a flag here is backward-compatible: an
 * existing plan without `features` resolves the whole map to its defaults
 * (everything off), so no tenant silently gains a paid capability.
 */
export const PlanFeaturesSchema = z.object({
  /**
   * Product cross-sell (Pro): unlocks the operator Products editor + lets the
   * server price add-on PRODUCTS onto a booking. Half of the two-condition
   * gate — the operator still opts in per-tenant via `productSales/config`.
   * Default false: `starter` tenants never see or sell products.
   */
  productSales: z.boolean().default(false),
});
export type PlanFeatures = z.infer<typeof PlanFeaturesSchema>;

export const PlanSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  basePrice: MoneySchema.describe('Per month, covers includedOutlets'),
  includedOutlets: z.literal(1).describe('MVP: always 1'),
  extraOutletPrice: MoneySchema.describe('Per extra outlet per month'),
  currency: CurrencySchema,
  /** Sprint 80 — one-time fees charged on first invoice; both default 0. */
  fees: PlanFeesSchema.default({ signupSen: 0, activationSen: 0 }),
  /** Per-plan capability flags — all default OFF (see PlanFeaturesSchema). */
  features: PlanFeaturesSchema.default({}),
});
export type Plan = z.infer<typeof PlanSchema>;

export const TenantStatusSchema = z.enum(['trial', 'active', 'past_due', 'suspended', 'archived']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

/**
 * Super-admin-granted trust / status recognition on an operator
 * (growth / status moat §09). NON-money: these flags never touch billing,
 * commissions, plan, or access — they are a public trust signal only.
 *
 * Both default false and the whole object is OPTIONAL on the profile, so
 * pre-existing tenants validate unchanged (absent === neither granted).
 * When set, the flags project onto the public `outletDirectory` row via
 * `buildDirectoryRow` and render as tasteful badges on the `/find` directory
 * (a verified check + a founding pill) — status that operators want to keep
 * (retention) and that signals trust to customers.
 *
 * Granted / revoked ONLY by a platform super-admin: the tenant-doc write rule
 * (infra/firestore.rules) gates updates to `isSuperAdmin()` and pins the
 * economically-sensitive profile fields, so recognition can only move under a
 * super-admin session. v1 is fully owner-controlled — there is no auto-grant.
 */
export const TenantRecognitionSchema = z.object({
  verified: z
    .boolean()
    .default(false)
    .describe('Super-admin-verified operator — renders a trust check on /find'),
  founding: z
    .boolean()
    .default(false)
    .describe('Founding operator of the network — renders a status pill on /find'),
});
export type TenantRecognition = z.infer<typeof TenantRecognitionSchema>;

export const TenantAcquisitionSchema = z.object({
  channel: z.enum(['agent', 'direct', 'referral']),
  agentId: ShortIdSchema.optional().describe('Set if channel = agent'),
  agentCodeUsed: z.string().optional().describe('Raw code typed at signup, for audit'),
  signedUpAt: TimestampSchema,
});
export type TenantAcquisition = z.infer<typeof TenantAcquisitionSchema>;

/**
 * Sprint 91 — Malaysia SST tax configuration per tenant.
 *
 * Service Tax (SST) is 8% on services in Malaysia as of March 2024.
 * Tenants whose annual revenue exceeds the SST registration threshold
 * (RM 500,000 for most services) MUST register and charge SST. Smaller
 * tenants can stay tax-free.
 *
 * Optional on the schema — tenants without `tax.registered === true`
 * generate SST-free invoices identical to the pre-Sprint-91 shape.
 * Registered tenants get an `sst` line item added to every monthly
 * invoice; the rendered tax-invoice PDF carries the SST number +
 * legal entity per MY Royal Customs format.
 *
 * Commission math stays tax-neutral: the `sst` line kind is ignored by
 * the existing agent/recruiter commission helpers (their switches drop
 * through the default branch on unknown kinds). SST flows to government,
 * not to anyone's earnings.
 */
/**
 * WHERE THIS LIVES: `tenants/{tid}/private/tax` — NOT on the tenant profile.
 *
 * It sat on `profile.tax` until 2026-08-29, and `firestore.rules` grants
 * `match /tenants/{tid} { allow read: if true }` so the unauthenticated
 * customer PWA shell can boot from a hostname. That made every field below
 * world-readable, including `companyRegistration` — which, as its own comment
 * says, holds the owner's **NRIC when the business is a sole proprietorship**,
 * the common shape for a small Malaysian carwash — plus `registeredAddress`,
 * which for a sole trader is usually their home. Tenant ids are not secret:
 * `slugToTenantId` and the public `outletDirectory` both hand them out.
 *
 * So an operator's national identity number and home address were readable by
 * anyone who could type a URL. Nothing in the product needed them there: the
 * only readers are the invoice PDF renderer and the monthly biller, both
 * server-side on the Admin SDK, plus the operator's own billing screen.
 *
 * Moving it to a subcollection keeps the public read that the boot path
 * actually needs (business name, branding) and gates the identity data behind
 * owner/manager, the same bar as `invoices`.
 */
export const TaxConfigSchema = z.object({
  registered: z.boolean().default(false).describe('True once SST registration is active'),
  sstNumber: z
    .string()
    .max(40)
    .optional()
    .describe('SST registration number (e.g., "W10-1234-5678901")'),
  /** Rate as a decimal. MY default 0.08 (8%) since March 2024. */
  rate: z.number().min(0).max(1).default(0.08),
  /** Legal entity name for the tax invoice. Falls back to businessName if absent. */
  companyName: z.string().max(120).optional(),
  /** SSM company registration number / NRIC for sole prop. */
  companyRegistration: z.string().max(40).optional(),
  /** Registered address for the tax invoice. Falls back to outlet address if absent. */
  registeredAddress: z.string().max(500).optional(),
  /** When the SST registration was last verified by super-admin (audit). */
  registeredAt: TimestampSchema.optional(),
});
export type TaxConfig = z.infer<typeof TaxConfigSchema>;

export const TenantProfileSchema = z.object({
  platformBrandId: SlugSchema.describe(
    'Which platform brand this tenant belongs to (ADR-0017). Immutable after signup.',
  ),
  businessName: z.string().min(1).max(100),
  ownerUid: UidSchema,
  planId: z.string(),
  status: TenantStatusSchema,
  slug: SlugSchema,
  contact: z.object({
    phone: z.string(),
    email: z.string().email(),
  }),
  locale: LocaleSchema,
  timezone: z.string().default('Asia/Kuala_Lumpur'),
  acquisition: TenantAcquisitionSchema,
  /**
   * Whether this operator takes part in peer benchmarking (gap G24).
   *
   * Optional and defaulting to absent, which reads as NOT participating: an
   * operator's figures must never join a cross-tenant aggregate because a field
   * defaulted true or because they never found a setting. The publisher filters
   * on this in the QUERY, so a non-participant's numbers are never even loaded.
   */
  /**
   * The operator asked to see every capability, skipping progressive disclosure
   * (gap G20).
   *
   * Optional and absent by default, which means "use the staging". Our guess
   * about someone's readiness is not better than their knowledge of their own
   * business, so this overrides it entirely and permanently.
   */
  showAllFeatures: z
    .boolean()
    .optional()
    .describe('Operator opted out of progressive disclosure; absent = staged.'),
  benchmarksOptIn: z
    .boolean()
    .optional()
    .describe('Operator opted in to anonymous peer benchmarking; absent = no.'),
  /**
   * Sprint 91 — optional SST config. Absent for tenants below the MY
   * registration threshold OR pre-Sprint-91 tenants. When `tax.registered`
   * is true, monthlyBilling adds an `sst` line to every invoice and the
   * PDF renders MY-compliant tax-invoice format.
   */
  // `tax` deliberately does NOT live here — see TaxConfigSchema above. It is
  // `tenants/{tid}/private/tax`, because this document is world-readable.

  /**
   * Super-admin-granted trust / status recognition (growth §09). Optional +
   * absent by default so existing tenants validate unchanged. When present,
   * the verified / founding flags project onto the public `outletDirectory`
   * row (`buildDirectoryRow`) and render as badges on `/find`. Only a
   * super-admin can write these (firestore.rules tenant-doc gate).
   */
  recognition: TenantRecognitionSchema.optional(),
  /**
   * False until the operator completes the first-outlet onboarding wizard
   * (outlet name/address, business hours, optional logo). Optional in the
   * schema so existing pre-Sprint-8 tenants don't fail validation.
   */
  onboardingCompleted: z.boolean().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type TenantProfile = z.infer<typeof TenantProfileSchema>;

export const SubscriptionStateSchema = z.object({
  status: TenantStatusSchema,
  outletCount: z.number().int().nonnegative(),
  nextBillingDate: TimestampSchema,
  paymentMethod: z.enum(['billplz_fpx', 'hitpay_duitnow_qr', 'manual_transfer']),
  trialEndsAt: TimestampSchema.optional(),
  suspendedAt: TimestampSchema.optional(),
  suspensionReason: z.string().optional(),
});
export type SubscriptionState = z.infer<typeof SubscriptionStateSchema>;

/**
 * Subscription audit event. Lives at `tenants/{tid}/subscriptionEvents/{eventId}`.
 *
 * Every mid-cycle change to outlet count (add today / remove next month) writes
 * one of these so the monthly billing job can attach prorations onto the next
 * invoice and the super-admin has a clean reconstruction of what happened when.
 *
 * Discriminated union on `kind` so new event types can be added without
 * breaking existing readers.
 */
export const SubscriptionEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('outlet_added'),
    id: ShortIdSchema,
    at: TimestampSchema,
    by: z.object({
      uid: UidSchema,
      role: z.enum(['owner', 'manager']).describe('Role at time of action'),
    }),
    outletId: z.string().min(1),
    outletSlug: SlugSchema,
    outletName: z.string().min(1),
    planId: z.string().min(1),
    /** Sen-integer amount charged for the partial month (today through end-of-month, inclusive). */
    prorationAmount: MoneySchema,
    currency: CurrencySchema,
    day: z.number().int().min(1).max(31),
    daysInMonth: z.number().int().min(28).max(31),
    /** outletCount on the subscription doc AFTER this event was applied. */
    outletCountAfter: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('outlet_removal_scheduled'),
    id: ShortIdSchema,
    at: TimestampSchema,
    by: z.object({
      uid: UidSchema,
      role: z.enum(['owner', 'manager']),
    }),
    outletId: z.string().min(1),
    outletSlug: SlugSchema,
    outletName: z.string().min(1),
    /**
     * When the outlet will actually be archived AND outletCount decrements.
     * End of the current month at 23:59:59 in Asia/Kuala_Lumpur. The outlet
     * is `soft_closed` from `at` until this moment (no new bookings, but
     * existing tickets complete). Operator pays full price for the partial
     * month — no refund (matches platform billing policy).
     */
    effectiveAt: TimestampSchema,
    /** outletCount AFTER the eventual decrement (informational; preview value). */
    outletCountAfterEffective: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('outlet_removal_cancelled'),
    id: ShortIdSchema,
    at: TimestampSchema,
    by: z.object({
      uid: UidSchema,
      role: z.enum(['owner', 'manager']),
    }),
    outletId: z.string().min(1),
    outletSlug: SlugSchema,
    outletName: z.string().min(1),
    /** The id of the `outlet_removal_scheduled` event being cancelled. */
    cancelledEventId: ShortIdSchema,
  }),
  z.object({
    /**
     * Written by the scheduled `processOutletRemovals` job (Sprint 24) when
     * a previously-scheduled removal hits its `effectiveAt`. Flips the outlet
     * `profile.status` to `archived` and decrements `subscription.outletCount`
     * in the same transaction. Customer data + ticketsHistory stay in place
     * for 90-day retention per the original plan.
     */
    kind: z.literal('outlet_archived'),
    id: ShortIdSchema,
    at: TimestampSchema,
    /** Who triggered the archival: the system job or a manual super-admin force-run. */
    by: z.object({
      uid: UidSchema,
      role: z.enum(['system', 'superadmin']),
    }),
    outletId: z.string().min(1),
    outletSlug: SlugSchema,
    outletName: z.string().min(1),
    /** Back-reference to the original `outlet_removal_scheduled` event. */
    scheduledEventId: ShortIdSchema,
    /** outletCount on subscription/current AFTER this archival applied. */
    outletCountAfter: z.number().int().nonnegative(),
  }),
]);
export type SubscriptionEvent = z.infer<typeof SubscriptionEventSchema>;

/**
 * The stored document at `tenants/{tid}/private/tax`.
 *
 * `id` is optional because repositories parse `doc.data()` and then attach the
 * document id (ADR-0026); a required `id` would make every read fail Zod's
 * object-strip before the id is put back — the exact bug that dead-ended every
 * outlet-scoped dashboard route in July.
 */
export const TenantTaxDocSchema = TaxConfigSchema.extend({
  id: z.string().optional(),
});
export type TenantTaxDoc = z.infer<typeof TenantTaxDocSchema>;

/** The one place that spells this path, so a typo cannot split reads from writes. */
export const TENANT_PRIVATE_COLLECTION = 'private';
export const TENANT_TAX_DOC_ID = 'tax';
export function tenantTaxPath(tenantId: string): string {
  return `tenants/${tenantId}/${TENANT_PRIVATE_COLLECTION}/${TENANT_TAX_DOC_ID}`;
}
